import React, { useEffect, useState } from "react";
import { TextLabel } from "../../components/style";
import { EMAIL_STATUS, getEmailAudienceDisplayName, getEmailAudienceUsersDisplayName } from "../../constants";
import {
    LottieLoadingIndicator,
    SmallActionButton,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalSectionTitle,
    StandardModalState,
    StandardModalTable,
    StandardModalTableScroll,
    StandardModalTableSection,
    UserSelectionAvatar,
    UserSelectionCellText,
    UserSelectionMetricCopy,
    UserSelectionMetricDescription,
    UserSelectionMetricSummary,
    UserSelectionMetricTitle,
    UserSelectionMetricValue,
    UserSelectionNameCell,
    UserSelectionTable
} from "src/components";
import { USER_GROUP } from "src/constants/perks";
import { getContacts } from "src/scenes/EmailCampaigns/api/contactsApi";
import { generateNameInitials } from "src/utils/helpers";
import Toast from "src/components/Toast";

const USER_LIST_AUDIENCE_NAMES = [USER_GROUP.EMPLOYEES, USER_GROUP.CLIENTS];

const getAudienceType = (audience) => USER_LIST_AUDIENCE_NAMES.includes(audience.name)
    ? "Me Business Users"
    : "Custom Audience";

const getContactFullName = (contact) => [contact.firstName, contact.lastName].filter(Boolean).join(" ");

function AudienceContactsModal({
    selectedAudience,
    contactUuidFilter,
    onClose
}) {
    const [contactsIsFetching, setContactsIsFetching] = useState(false);
    const [contactsList, setContactsList] = useState([]);

    useEffect(() => {
        const getAllContacts = async () => {
            if (!selectedAudience?.uuid) {
                return;
            }

            setContactsIsFetching(true);
            try {
                const response = await getContacts(selectedAudience.uuid);
                const contacts = response.contacts || [];
                const filteredContacts = contactUuidFilter
                    ? contacts.filter(contact => contactUuidFilter.includes(contact.uuid))
                    : contacts;
                setContactsList(filteredContacts);
            } catch (error) {
                console.log("Error: ", error);
                Toast.error("Error occurred while fetching contacts.");
            } finally {
                setContactsIsFetching(false);
            }
        };

        getAllContacts();
    }, [selectedAudience?.uuid]);

    return (
        <StandardModal
            open={Boolean(selectedAudience)}
            onClose={onClose}
            widthMode="table"
            title={selectedAudience ? getEmailAudienceDisplayName(selectedAudience.name) : "Audience Contacts"}
            description="Review the contacts included from this audience for the selected campaign."
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton type="button" onClick={onClose}>
                        Close
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <UserSelectionMetricSummary>
                    <UserSelectionMetricCopy>
                        <UserSelectionMetricTitle>Contacts Included</UserSelectionMetricTitle>
                        <UserSelectionMetricDescription>
                            These contacts are part of the campaign recipient list for this audience.
                        </UserSelectionMetricDescription>
                    </UserSelectionMetricCopy>
                    <UserSelectionMetricValue>{contactsList.length.toLocaleString("en-us")}</UserSelectionMetricValue>
                </UserSelectionMetricSummary>
            </StandardModalSection>

            <StandardModalSection>
                {contactsIsFetching ? (
                    <StandardModalState minHeight="20rem">
                        <LottieLoadingIndicator />
                    </StandardModalState>
                ) : (
                    <StandardModalTableSection>
                        <StandardModalTableScroll maxHeight="min(30rem, calc(100vh - 27rem))">
                            <UserSelectionTable>
                                <StandardModalTable minWidth="760px">
                                    <thead>
                                        <tr>
                                            <th style={{ width: "30%" }}>Name</th>
                                            <th style={{ width: "30%" }}>Email</th>
                                            <th style={{ width: "20%" }}>Mobile</th>
                                            <th style={{ width: "20%" }}>Reference</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contactsList.length === 0 ? (
                                            <tr>
                                                <td colSpan="4">
                                                    <StandardModalState minHeight="8rem" border="0">
                                                        No contacts found
                                                    </StandardModalState>
                                                </td>
                                            </tr>
                                        ) : contactsList.map((contact, index) => {
                                            const fullName = getContactFullName(contact);
                                            const initials = generateNameInitials(contact.firstName, contact.lastName);

                                            return (
                                                <tr key={contact.uuid || index}>
                                                    <td>
                                                        <UserSelectionNameCell>
                                                            <UserSelectionAvatar>{initials}</UserSelectionAvatar>
                                                            <UserSelectionCellText>{fullName || "-"}</UserSelectionCellText>
                                                        </UserSelectionNameCell>
                                                    </td>
                                                    <td>{contact.email || "-"}</td>
                                                    <td>{contact.mobileNumber || "-"}</td>
                                                    <td>{contact.reference || "-"}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </StandardModalTable>
                            </UserSelectionTable>
                        </StandardModalTableScroll>
                    </StandardModalTableSection>
                )}
            </StandardModalSection>
        </StandardModal>
    );
}

export default function TableAudience(props) {
    const { audiences, campaignName, recipientContacts, totalRecipients, emailStatus } = props;
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [selectedAudience, setSelectedAudience] = useState(null);
    const [isViewingContacts, setIsViewingContacts] = useState(false);

    const campaignAudiences = audiences || [];
    const campaignRecipientContacts = recipientContacts || [];
    const recipientContactUuids = campaignRecipientContacts.map(contact => contact.uuid);
    const sentAudiences = campaignAudiences
        .filter(audience => audience.contacts.some(contact => recipientContactUuids.includes(contact.uuid)));
    const sentUserListAudiences = sentAudiences
        .filter(audience => USER_LIST_AUDIENCE_NAMES.includes(audience.name));
    const sentCustomAudiences = sentAudiences
        .filter(audience => !USER_LIST_AUDIENCE_NAMES.includes(audience.name));
    const audienceRows = [...sentUserListAudiences, ...sentCustomAudiences];
    const alreadySent = [EMAIL_STATUS.SENT, EMAIL_STATUS.QUEUED, EMAIL_STATUS.SENDING, EMAIL_STATUS.FAILED].includes(emailStatus);

    const onViewAudience = (audience) => {
        setSelectedAudience({
            uuid: audience.uuid,
            name: audience.name,
        });
        setIsViewingContacts(true);
    };

    const getTotalByAudienceName = (audienceName) => {
        const audience = campaignAudiences.find(audience => audience.name === audienceName);
        if (!audience) {
            return 0;
        }
        const contacts = campaignRecipientContacts.filter(contact => audience.uuid === contact.audienceUuid);
        return contacts.length;
    };

    return (
        <>
            <div>
                <div
                    style={{
                        width: "91px",
                        height: "26px",
                        backgroundColor: "#FFFFFF",
                        backgroundClip: "padding-box",
                        position: "0% 0%",
                        border: "1px solid #171A22",
                        borderRadius: "7px",
                        opacity: 1,
                        paddingLeft: "5px",
                        paddingRight: "5px",
                        textAlign: "center",
                        alignItems: "center",
                        justifyContent: "center",
                        display: "flex",
                        color: "#171A22",
                        fontSize: "12px",
                        boxShadow: "0px 3px 6px #00000029",
                        cursor: "pointer",
                    }}
                    onClick={() => setShowBreakdown(true)}
                >
                    View
                </div>
                <TextLabel>{`${totalRecipients ? totalRecipients : 0} recipients`}</TextLabel>
            </div>

            <StandardModal
                open={showBreakdown}
                onClose={() => setShowBreakdown(false)}
                widthMode="wide"
                title="Audience Breakdown"
                description={`This email campaign ${alreadySent ? "was" : "will be"} sent to the following recipients.`}
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton type="button" onClick={() => setShowBreakdown(false)}>
                            Close
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                )}
            >
                <StandardModalSection>
                    <UserSelectionMetricSummary>
                        <UserSelectionMetricCopy>
                            <UserSelectionMetricTitle>{campaignName || "Campaign Audience"}</UserSelectionMetricTitle>
                            <UserSelectionMetricDescription>
                                Recipient count across Me Business users and custom audiences.
                            </UserSelectionMetricDescription>
                        </UserSelectionMetricCopy>
                        <UserSelectionMetricValue>{(totalRecipients || 0).toLocaleString("en-us")}</UserSelectionMetricValue>
                    </UserSelectionMetricSummary>
                </StandardModalSection>

                <StandardModalSection>
                    <StandardModalSectionTitle>Recipient Sources</StandardModalSectionTitle>
                    <StandardModalTableSection>
                        <StandardModalTableScroll maxHeight="min(32rem, calc(100vh - 26rem))">
                            <StandardModalTable minWidth="720px">
                                <thead>
                                    <tr>
                                        <th style={{ width: "28%" }}>Source Type</th>
                                        <th style={{ width: "42%" }}>Audience</th>
                                        <th style={{ width: "16%" }}>Recipients</th>
                                        <th style={{ width: "14%" }}>Contacts</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {audienceRows.length === 0 ? (
                                        <tr>
                                            <td colSpan="4">
                                                <StandardModalState minHeight="8rem" border="0">
                                                    No audience recipients found
                                                </StandardModalState>
                                            </td>
                                        </tr>
                                    ) : audienceRows.map(audience => (
                                        <tr key={audience.uuid}>
                                            <td>{getAudienceType(audience)}</td>
                                            <td style={{ fontWeight: 600, textTransform: USER_LIST_AUDIENCE_NAMES.includes(audience.name) ? "capitalize" : "none" }}>
                                                {getEmailAudienceUsersDisplayName(audience.name)}
                                            </td>
                                            <td style={{ fontWeight: 800 }}>
                                                {getTotalByAudienceName(audience.name).toLocaleString("en-us")}
                                            </td>
                                            <td>
                                                <SmallActionButton type="button" onClick={() => onViewAudience(audience)}>
                                                    View
                                                </SmallActionButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </StandardModalTable>
                        </StandardModalTableScroll>
                    </StandardModalTableSection>
                </StandardModalSection>
            </StandardModal>

            {isViewingContacts && (
                <AudienceContactsModal
                    selectedAudience={selectedAudience}
                    contactUuidFilter={recipientContactUuids}
                    onClose={() => setIsViewingContacts(false)}
                />
            )}
        </>
    );
}
